// Need to use the React-specific entry point to import createApi
import { createApi } from "@reduxjs/toolkit/query/react";
import { State, Term } from "../../../api/src/types";
import { baseQuery } from "./utils";
import type {
  StatesAdminGetResponse,
  StatesGetResponse,
  StatePatchRequest,
  StatePatchResponse,
  DocumentTemplateStatePatchRequest,
  DocumentTemplateStatePatchResponse,
  StateAttorneysGetResponse,
  StateAttorneyOrderPatchRequest,
  StateAttorneyOrderPatchResponse,
} from "../../../api/src/states/route";
import type {
  TermPatchRequest,
  TermPatchResponse,
} from "../../../api/src/terms/route";

export const statesApi = createApi({
  reducerPath: "statesApi",
  baseQuery,
  tagTypes: ["states", "terms"],
  endpoints: (builder) => ({
    getStates: builder.query<StatesGetResponse, { isActive?: boolean }>({
      query: ({ isActive }) => ({
        url: `states${isActive !== undefined ? `?isActive=${isActive}` : ""}`,
        method: "GET",
      }),
      providesTags: ["states"],
    }),
    getStatesAdmin: builder.query<StatesAdminGetResponse, void>({
      query: () => ({
        url: `states/admin`,
        method: "GET",
      }),
      providesTags: ["states"],
    }),
    getUserStateById: builder.query<State, { id: string; stateId: string }>({
      query: ({ id, stateId }) => ({
        url: `users/${id}/state/${stateId}`,
        method: "GET",
      }),
      providesTags: ["states"],
    }),
    getTermsByStateIdAndTemplateId: builder.query<
      Term[],
      { stateId: string; documentTemplateId: string }
    >({
      query: ({ stateId, documentTemplateId }) => ({
        url: `terms?stateId=${stateId}&documentTemplateId=${documentTemplateId}`,
        method: "GET",
      }),
      providesTags: ["terms"],
    }),
    updateStateAdmin: builder.mutation<
      StatePatchResponse,
      StatePatchRequest & { id: string }
    >({
      query: ({ id, ...patch }) => ({
        url: `states/${id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: ["states"],
    }),
    updateTermAdmin: builder.mutation<
      TermPatchResponse,
      TermPatchRequest & {
        key: string;
        stateId: string;
        documentTemplateId: string;
      }
    >({
      query: ({ key, stateId, documentTemplateId, ...patch }) => ({
        url: `terms/${key}/${stateId}/${documentTemplateId}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: ["terms"],
    }),
    updateDocumentTemplateState: builder.mutation<
      DocumentTemplateStatePatchResponse,
      DocumentTemplateStatePatchRequest & {
        stateId: string;
        documentTemplateId: string;
      }
    >({
      query: ({ stateId, documentTemplateId, ...patch }) => ({
        url: `states/${stateId}/document-template-states/${documentTemplateId}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: ["states"],
    }),
    getStateAttorneys: builder.query<StateAttorneysGetResponse, string>({
      query: (stateId) => ({
        url: `states/admin/${stateId}/attorneys`,
        method: "GET",
      }),
      providesTags: ["states"],
    }),
    updateStateAttorneyOrder: builder.mutation<
      StateAttorneyOrderPatchResponse,
      StateAttorneyOrderPatchRequest & { stateId: string }
    >({
      query: ({ stateId, ...body }) => ({
        url: `states/admin/${stateId}/attorney-order`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["states"],
    }),
  }),
});

export const {
  useGetStatesQuery,
  useGetStatesAdminQuery,
  useGetUserStateByIdQuery,
  useGetTermsByStateIdAndTemplateIdQuery,
  useUpdateStateAdminMutation,
  useUpdateTermAdminMutation,
  useUpdateDocumentTemplateStateMutation,
  useGetStateAttorneysQuery,
  useUpdateStateAttorneyOrderMutation,
} = statesApi;
